(() => {
  if (customElements.get('atomic-focus-motion')) return;
  customElements.define('atomic-focus-motion', class extends HTMLElement {
    connectedCallback() {
      this.elapsed = 0;
      this.visible = false;
      this.paused = false;
      this.finished = false;
      this.dataset.paused = 'false';
      this.dataset.finished = 'false';
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.button = this.querySelector('button');
      this.button.setAttribute('aria-label', 'Pause focus animation');
      this.button.querySelector('span').textContent = 'Pause';
      this.querySelectorAll('[data-motion-tail]').forEach(dot => dot.remove());
      this.tracks = [...this.querySelectorAll('[data-motion-kind]')].map(group => {
        const path = group.parentElement.querySelector('.la-focus-motion__guide');
        const trail = group.parentElement.querySelector('.la-focus-motion__restless-trail');
        if (trail) trail.setAttribute('d', path.getAttribute('d'));
        const dots = [group.querySelector('circle')];
        if (group.dataset.motionKind === 'directed') {
          for (let index = 1; index <= 18; index++) {
            const dot = dots[0].cloneNode();
            dot.removeAttribute('style');
            dot.setAttribute('class', 'la-focus-motion__dot');
            dot.setAttribute('data-motion-tail', '');
            group.prepend(dot);
            dots.push(dot);
          }
        }
        const length = path.getTotalLength();
        const tailPoints = dots.slice(1).map((_, index) => {
          const distance = index * length / (dots.length - 1);
          return { distance, point: path.getPointAtLength(distance) };
        });
        // Sample once so both dots share a horizontal position, regardless of curve length.
        const samples = Array.from({ length: 257 }, (_, index) => {
          const distance = index * length / 256;
          return { distance, x: path.getPointAtLength(distance).x };
        });
        return { path, length, dots, trail, samples, tailPoints };
      });
      this.tick = timestamp => {
        if (this.lastTime != null) this.elapsed += Math.min(timestamp - this.lastTime, 64);
        this.lastTime = timestamp;
        const progress = Math.min(this.elapsed / 6000, 1);
        this.draw(progress, Math.min(this.elapsed / 364, 1));
        if (progress === 1) {
          this.finished = true;
          this.updateControl();
          return;
        }
        this.frame = requestAnimationFrame(this.tick);
      };
      this.sync = () => {
        cancelAnimationFrame(this.frame);
        this.lastTime = null;
        this.button.hidden = this.reduced.matches;
        if (this.reduced.matches) {
          this.draw(1, 1);
          this.finished = true;
          this.updateControl();
        } else if (this.visible && !this.paused && !this.finished && !document.hidden) this.frame = requestAnimationFrame(this.tick);
      };
      this.toggle = () => {
        if (this.finished) {
          this.elapsed = 0;
          this.finished = false;
          this.paused = false;
          this.draw(0, 1);
        } else this.paused = !this.paused;
        this.updateControl();
        this.sync();
      };
      this.button.addEventListener('click', this.toggle);
      this.reduced.addEventListener('change', this.sync);
      document.addEventListener('visibilitychange', this.sync);
      this.draw(0, 1);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.sync();
      }, { threshold: .15 });
      this.observer.observe(this);
      this.sync();
    }
    updateControl() {
      const label = this.finished ? 'Replay' : this.paused ? 'Play' : 'Pause';
      this.dataset.paused = String(this.paused || this.finished);
      this.dataset.finished = String(this.finished);
      this.button.setAttribute('aria-label', `${label} focus animation`);
      this.button.setAttribute('title', label);
      this.button.querySelector('span').textContent = label;
    }
    draw(progress, opacity) {
      this.tracks.forEach(({ path, length, dots, trail, samples, tailPoints }) => {
        const x = samples[0].x + progress * (samples[256].x - samples[0].x);
        let low = 0;
        let high = 256;
        while (high - low > 1) {
          const middle = (low + high) >> 1;
          if (samples[middle].x < x) low = middle;
          else high = middle;
        }
        const fraction = (x - samples[low].x) / (samples[high].x - samples[low].x || 1);
        const distance = samples[low].distance + fraction * (samples[high].distance - samples[low].distance);
        dots.forEach((dot, index) => {
          // Leave equal-sized periods in place, like the headline's revealed trail.
          const position = index ? tailPoints[index - 1].distance : distance;
          const point = index ? tailPoints[index - 1].point : path.getPointAtLength(distance);
          dot.setAttribute('cx', point.x);
          dot.setAttribute('cy', point.y);
          if (index) {
            // The headline's .10/.20/.34/.52/.72 opacity ramp, extended along the track.
            const ramp = [.1, .2, .34, .52, .72];
            const fraction = Math.min(1, position / Math.max(1, distance));
            const segment = Math.min(3, Math.floor(fraction * 4));
            const strength = ramp[segment] + (ramp[segment + 1] - ramp[segment]) * (fraction * 4 - segment);
            const revealed = Math.max(0, Math.min(1, (distance - position - 16) / 8));
            dot.style.opacity = opacity * strength * revealed;
          } else dot.style.opacity = opacity;
        });
        if (trail) {
          const tail = Math.min(100, distance);
          trail.setAttribute('stroke-dasharray', `0 ${Math.max(0, distance - tail)} ${tail} ${length}`);
          trail.style.opacity = opacity * .8;
        }
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.toggle);
      this.reduced?.removeEventListener('change', this.sync);
      document.removeEventListener('visibilitychange', this.sync);
    }
  });
})();
